import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { Layers } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isApiError, readCsrfToken } from '../../../api/client';
import { salesApi } from '../../../api/endpoints';
import { organizationQuery, queryKeys, useMe } from '../../../api/queries';
import type { BulkResult, BulkTask, SalesDocumentSummary } from '../../../api/types';
import { useAccess, type Permission } from '../../../app/access';
import { useI18n } from '../../../i18n/I18nProvider';
import { sumDecimals } from '../../../lib/decimal';
import { NARROW_QUERY, PHONE_QUERY, useMediaQuery } from '../../../lib/hooks';
import { buildStoredZip } from '../../../lib/zip';
import { Button } from '../../../ui/Button';
import { Skeleton } from '../../../ui/Feedback';
import { Page, useDocumentTitle } from '../../../ui/Layout';
import { QueryError } from '../../../ui/QueryError';
import { Stepper } from '../../../ui/Stepper';
import { useToast } from '../../../ui/Toast';
import type { InvoiceListSearch } from '../InvoiceListPage';
import salesStyles from '../Sales.module.css';
import { MockLabel } from '../status';
import styles from './Batch.module.css';
import { BatchBar } from './BatchBar';
import {
  ClearConfirm,
  DiscardConfirm,
  LeaveResultsConfirm,
  RetryConfirm,
  SendConfirm,
} from './BatchDialogs';
import { BatchRail, BatchRailToggle } from './BatchRail';
import { BatchReview } from './BatchReview';
import {
  BATCH_TASKS,
  addItems,
  clearItems,
  emptyBatch,
  isFull,
  itemOf,
  refreshItems,
  removeItems,
  reviewIsCurrent,
  setTask,
  useBatch,
  type BatchItem,
} from './batchStore';
import { criteriaOf, type BatchCriteria } from './criteria';
import { DocumentsStep, type PutAllNotice } from './DocumentsStep';
import { ResultsPanel, resultsSummary } from './ResultsPanel';
import {
  isLive,
  outcomeCounts,
  rereadResult,
  type BeforeCall,
  resultOrder,
  rowState,
  unresolvedCount,
} from './rules';
import { TaskStep } from './TaskStep';

type StepId = 'task' | 'documents' | 'review';
const STEPS: readonly StepId[] = ['task', 'documents', 'review'];

/** The task's request permission: a task the user may not run is not shown (BD-11). */
const TASK_PERMISSION: Record<BulkTask, Permission> = {
  send: 'invoice.submit',
  pdf: 'invoice.view',
  discard: 'invoice.edit',
};
const UNDO_MS = 6000;
const PDF_PARALLEL = 3;

/**
 * MF-INV-008, the batch page (BLOCK-1B-BULK-DESIGN, BD-1…BD-12): «تجهيز حزمة مستندات», a guided
 * flow — ① the task, ② the documents, ③ the review and run — then the results on the same page,
 * beside the named batch «الحزمة». It is reached from the list's find panel with the list's
 * criteria; the list itself has no multi-document state.
 */
export function BatchPage() {
  const { orgId = '' } = useParams({ strict: false });
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const searchKey = JSON.stringify(search);
  const criteria = useMemo(
    () => criteriaOf(search),
    // The parsed search object is new on every render; its content is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchKey],
  );
  const me = useMe();
  const userId = me.data?.user.id;
  if (!userId)
    return (
      <Page>
        <Skeleton height="10rem" />
      </Page>
    );
  return <BatchFlow key={`${orgId}.${userId}`} orgId={orgId} userId={userId} criteria={criteria} />;
}

type Running = null | 'server' | 'file';

function BatchFlow({
  orgId,
  userId,
  criteria,
}: {
  orgId: string;
  userId: string;
  criteria: BatchCriteria;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const access = useAccess(orgId);
  const org = useQuery(organizationQuery(orgId));
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [batch, update, getBatch] = useBatch(orgId, userId);
  useDocumentTitle(t.batch.title);

  const tasks = BATCH_TASKS.filter((task) => access.can(TASK_PERMISSION[task]));
  const task = batch.task && tasks.includes(batch.task) ? batch.task : null;
  const items = batch.items;
  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const [step, setStep] = useState<StepId>(() =>
    batch.run ? 'review' : batch.task ? 'documents' : 'task',
  );
  const view = step === 'review' && batch.run ? 'results' : step;
  const [running, setRunning] = useState<Running>(null);
  const [fileProgress, setFileProgress] = useState({ done: 0, total: 0 });
  const [notice, setNotice] = useState<PutAllNotice>(null);
  const [railOpen, setRailOpen] = useState(false);
  const [dialog, setDialog] = useState<null | 'send' | 'discard' | 'retry' | 'clear' | 'leave'>(
    null,
  );
  /** RIB-01: what happens once the user agrees to leave unsettled results (a new batch or task). */
  const leaveThen = useRef<(() => void) | null>(null);
  const [retry, setRetry] = useState<null | {
    ids: string[];
    marks: Map<string, BulkResult>;
    versions: Record<string, number>;
  }>(null);
  // RIB-01 (DEC-046): once a run exists, the batch's documents stay as they are — the rail, the add
  // field and put-all are locked like during a run — until «ابدأ حزمة جديدة».
  const hasRun = batch.run !== null;
  const [retrying, setRetrying] = useState(false);
  const files = useRef(new Map<string, { name: string; data: Uint8Array }>());

  // ------------------------------------------------------------------ announcements and focus
  const [announcement, setAnnouncement] = useState('');
  const announce = useCallback((message: string) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(message));
  }, []);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusSeq, setFocusSeq] = useState(0);
  useEffect(() => {
    if (focusSeq) headingRef.current?.focus();
  }, [focusSeq]);

  const stepTitles: Record<StepId, string> = {
    task: t.batch.steps.task,
    documents: t.batch.steps.documents,
    review: t.batch.steps.review,
  };
  const goTo = (next: StepId) => {
    if (next !== step) {
      setStep(next);
      announce(
        t.editor.steps.announce(
          String(STEPS.indexOf(next) + 1),
          String(STEPS.length),
          stepTitles[next],
        ),
      );
    }
    setFocusSeq((n) => n + 1);
  };

  // ------------------------------------------------------------------ the review (dry run)
  const locked = running !== null;
  const dryKey = task && ids.length ? `${task}:${ids.join(',')}` : null;
  const dry = useQuery({
    queryKey: [...queryKeys.org(orgId), 'sales', 'batch-dry-run', dryKey],
    queryFn: ({ signal }) =>
      salesApi.bulk(orgId, { action: task as BulkTask, ids, dryRun: true }, signal),
    enabled: view === 'review' && dryKey !== null && !locked,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const review =
    dry.data && dryKey && dry.data.action === task && !dry.isFetching ? dry.data.results : null;
  useEffect(() => {
    if (!dry.data || !task) return;
    const results = dry.data.results;
    update((s) => ({
      ...refreshItems(s, results),
      review: { task, ids: results.map((r) => r.id), results },
    }));
    // Only a new answer of the dry run is kept.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dry.data]);
  useEffect(() => {
    if (!review) return;
    const run = review.filter((r) => r.ok).length;
    announce(t.batch.review.summary(String(run), String(review.length - run)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review]);
  const willRun = review?.filter((r) => r.ok) ?? [];
  const facts = {
    count: willRun.length,
    total: sumDecimals(willRun.map((r) => r.totalAmount ?? '0')),
  };

  // ------------------------------------------------------------------ the batch's own controls
  const onAdd = (item: BatchItem) => {
    const result = addItems(getBatch(), [item]);
    if (!result.added) return;
    update(() => ({ ...result.state, run: null }));
    setNotice(isFull(result.state) ? { kind: 'full' } : null);
    announce(t.batch.announce.put(item.number ?? ''));
  };
  const onPutAll = (candidates: SalesDocumentSummary[], total: number) => {
    const result = addItems(getBatch(), candidates.map(itemOf));
    if (result.added) update(() => ({ ...result.state, run: null }));
    const full = isFull(result.state);
    const trimmed = result.left > 0 || (full && total > candidates.length);
    setNotice(trimmed ? { kind: 'trimmed', total } : full ? { kind: 'full' } : null);
    announce(
      `${t.batch.putAllDone(String(result.added))}${trimmed ? ` ${t.batch.trimmed(String(total))}` : full ? ` ${t.batch.full}` : ''}`,
    );
  };
  const onTakeOut = (item: BatchItem) => {
    update((s) => ({ ...removeItems(s, [item.id]), run: null }));
    setNotice(null);
    announce(t.batch.announce.out(item.number ?? ''));
  };
  const onDropSkipped = (skippedIds: string[]) => {
    update((s) => ({ ...removeItems(s, skippedIds), run: null }));
    setNotice(null);
  };
  const onClear = () => {
    setDialog(null);
    update(clearItems);
    setNotice(null);
  };
  const startNew = () => {
    setRetry(null);
    files.current.clear();
    update(() => emptyBatch());
    setNotice(null);
    goTo('task');
  };
  /** RIB-01: leaving results that are not settled (with ETA now, not completed, not confirmed) asks. */
  const leaving = (then: () => void) => {
    const run = getBatch().run;
    if (run && unresolvedCount(run.task, run.results) > 0) {
      leaveThen.current = then;
      setDialog('leave');
    } else then();
  };
  const onNew = () => leaving(startNew);
  const confirmLeave = () => {
    const then = leaveThen.current;
    leaveThen.current = null;
    setDialog(null);
    then?.();
  };

  // ------------------------------------------------------------------ the run
  /**
   * The call failed on the way (BD-7: the server goes on): every document of the call is re-read
   * with `ids`. A row whose state moved is "not confirmed"; an unchanged one did not get a result.
   * Both may be tried again; a repeat of a done action is a skip.
   */
  const reconcile = async (callIds: readonly string[]): Promise<BulkResult[]> => {
    if (!callIds.length) return [];
    const before = new Map(getBatch().items.map((i) => [i.id, i]));
    // RIB-02: "before the call" is the document as the review read it (the rail may hold newer
    // readings, RIB-04); the review's skips stand for documents that did not change since.
    const reviewed = new Map((getBatch().review?.results ?? []).map((r) => [r.id, r]));
    const beforeCall = new Map<string, BeforeCall>(
      callIds.flatMap((id) => {
        const r = reviewed.get(id);
        const item = before.get(id);
        const read =
          r?.status !== undefined
            ? { status: r.status, etaStatus: r.etaStatus ?? null, version: r.version ?? 0 }
            : item
              ? { status: item.status, etaStatus: item.etaStatus, version: item.version }
              : null;
        return read ? [[id, read] as const] : [];
      }),
    );
    let now: Map<string, SalesDocumentSummary>;
    try {
      const page = await salesApi.list(orgId, { ids: callIds.join(','), limit: 100 });
      now = new Map(page.items.map((d) => [d.id, d]));
    } catch {
      return callIds.map((id) => ({
        id,
        ok: false,
        code: 'network_error',
        ...snapshotOf(before.get(id)),
      }));
    }
    // RIB-02: a document at the task's end state is reported by that state, never as "not completed".
    const runTask = getBatch().run?.task ?? getBatch().task ?? 'send';
    return callIds.map((id) =>
      rereadResult(runTask, id, beforeCall.get(id), now.get(id), reviewed.get(id)),
    );
  };

  /** The PDF task's file phase (BD-9): each completed document's PDF, 3 at a time, then one ZIP. */
  const fetchFiles = async (results: BulkResult[]): Promise<BulkResult[]> => {
    const wanted = results.filter((r) => r.ok && !files.current.has(r.id));
    setFileProgress({ done: 0, total: wanted.length });
    const failed = new Map<string, string>();
    let done = 0;
    let next = 0;
    const worker = async () => {
      while (next < wanted.length) {
        const row = wanted[next++];
        if (!row) return;
        try {
          files.current.set(row.id, {
            name: `${(row.number ?? row.id).replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`,
            data: await fetchPdf(orgId, row.id),
          });
        } catch (error) {
          failed.set(row.id, (error as { code?: string }).code ?? 'internal_error');
        }
        done += 1;
        setFileProgress({ done, total: wanted.length });
      }
    };
    await Promise.all(Array.from({ length: PDF_PARALLEL }, worker));
    return results.map((r) =>
      failed.has(r.id) ? { ...r, ok: false, code: failed.get(r.id), details: { file: true } } : r,
    );
  };

  /** Saves ONE store-only ZIP of every completed document (BD-9). */
  const saveZip = async (results: readonly BulkResult[]) => {
    const completed = results.filter((r) => r.ok);
    const missing = completed.filter((r) => !files.current.has(r.id));
    if (missing.length) {
      setRunning('file');
      try {
        await fetchFiles(missing);
      } finally {
        setRunning(null);
      }
    }
    const entries = completed.flatMap((r) => {
      const file = files.current.get(r.id);
      return file ? [file] : [];
    });
    if (!entries.length) return;
    const blob = new Blob([buildStoredZip(entries)], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = zipName(new Date());
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const execute = async (
    kind: 'run' | 'retry',
    runTask: BulkTask,
    callIds: string[],
    versions: Record<string, number>,
  ) => {
    const startedAt = new Date().toISOString();
    setRetry(null); // a run or a retry replaces any marks of an earlier retry (RIM-07)
    update((s) =>
      kind === 'run' || !s.run
        ? {
            ...s,
            run: {
              task: runTask,
              ids: callIds,
              phase: 'running',
              results: [],
              order: [],
              startedAt,
            },
          }
        : { ...s, run: { ...s.run, phase: 'running' } },
    );
    setRunning('server');
    announce(t.batch.progress);
    let results: BulkResult[];
    try {
      results = (await salesApi.bulk(orgId, { action: runTask, ids: callIds, versions })).results;
    } catch (error) {
      if (isApiError(error) && [401, 403, 422].includes(error.status)) {
        // Refused as a whole: nothing ran and the batch is unchanged.
        toast.error(
          error,
          error.status === 422 && error.issues.some((i) => i.issue === 'too_big')
            ? t.batch.errors.tooMany
            : undefined,
        );
        update((s) =>
          kind === 'run'
            ? { ...s, run: null }
            : s.run
              ? { ...s, run: { ...s.run, phase: 'done' } }
              : s,
        );
        setRunning(null);
        return;
      }
      results = await reconcile(callIds);
    }
    if (runTask === 'pdf') {
      update((s) => (s.run ? { ...s, run: { ...s.run, phase: 'file' } } : s));
      setRunning('file');
      results = await fetchFiles(results);
    }
    // RIB-04: the rail shows each document as the run left it.
    update((s) => refreshItems(s, results));
    const prior = getBatch().run;
    const all =
      kind === 'retry' && prior && prior.results.length
        ? prior.results.map((r) => results.find((x) => x.id === r.id) ?? r)
        : results;
    update((s) => ({
      ...s,
      run:
        kind === 'retry' && prior && prior.results.length
          ? { ...prior, phase: 'done', results: all, lost: false }
          : {
              task: runTask,
              ids: callIds,
              phase: 'done',
              results,
              order: resultOrder(runTask, results),
              startedAt,
            },
    }));
    setRunning(null);
    if (runTask === 'pdf') await saveZip(all);
    void queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales'] });
    announce(
      `${t.batch.announce.finished} ${resultsSummary(t, outcomeCounts(runTask, all), String)}`,
    );
    setFocusSeq((n) => n + 1);
  };

  // ------------------------------------------------------------------ delete: the 6 s undo
  const [undo, setUndo] = useState<null | { kind: 'run' | 'retry' }>(null);
  const pending = useRef<null | {
    kind: 'run' | 'retry';
    ids: string[];
    versions: Record<string, number>;
  }>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startDiscard = (
    kind: 'run' | 'retry',
    callIds: string[],
    versions: Record<string, number>,
  ) => {
    pending.current = { kind, ids: callIds, versions };
    setUndo({ kind });
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => {
      undoTimer.current = null;
      const p = pending.current;
      pending.current = null;
      setUndo(null);
      if (p) void execute(p.kind, 'discard', p.ids, p.versions);
    }, UNDO_MS);
  };
  const cancelDiscard = () => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
    pending.current = null;
    setUndo(null);
  };
  // Leaving the page inside the window commits the confirmed delete (keepalive), as the single
  // delete does; the run is then shown as "lost" when the page is opened again.
  const commitNow = useRef<() => void>(() => undefined);
  commitNow.current = () => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    if (undoTimer.current) clearTimeout(undoTimer.current);
    update((s) => ({
      ...s,
      run: {
        task: 'discard',
        ids: p.ids,
        phase: 'running',
        results: s.run?.results ?? [],
        order: s.run?.order ?? [],
        startedAt: new Date().toISOString(),
      },
    }));
    void fetch(`/api/orgs/${encodeURIComponent(orgId)}/sales/documents/bulk`, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': readCsrfToken() ?? '' },
      body: JSON.stringify({ action: 'discard', ids: p.ids, versions: p.versions }),
    });
  };
  useEffect(() => {
    const onHide = () => commitNow.current();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      commitNow.current();
    };
  }, []);

  // ------------------------------------------------------------------ run and retry entry points
  const versionsOf = (results: readonly BulkResult[]) =>
    Object.fromEntries(
      results.flatMap((r) => (r.version !== undefined ? [[r.id, r.version] as const] : [])),
    );
  const onRun = () => {
    if (!task || !review) return;
    if (task === 'send') setDialog('send');
    else if (task === 'discard') setDialog('discard');
    else void execute('run', 'pdf', ids, versionsOf(review));
  };
  const confirmRun = () => {
    if (!task || !review) return;
    setDialog(null);
    if (task === 'discard') startDiscard('run', ids, versionsOf(review));
    else void execute('run', task, ids, versionsOf(review));
  };
  const onRetry = async () => {
    const run = batch.run;
    if (!run) return;
    const retryIds = run.results.filter((r) => rowState(run.task, r).retryable).map((r) => r.id);
    if (!retryIds.length) return;
    setRetrying(true);
    try {
      const plan = await salesApi.bulk(orgId, { action: run.task, ids: retryIds, dryRun: true });
      update((s) => refreshItems(s, plan.results));
      setRetry({
        ids: retryIds,
        marks: new Map(plan.results.map((r) => [r.id, r])),
        versions: versionsOf(plan.results),
      });
      setDialog('retry');
    } catch (error) {
      toast.error(error);
    } finally {
      setRetrying(false);
    }
  };
  const confirmRetry = () => {
    const r = retry;
    const run = batch.run;
    setDialog(null);
    setRetry(null);
    if (!r || !run) return;
    if (run.task === 'discard') startDiscard('retry', r.ids, r.versions);
    else void execute('retry', run.task, r.ids, r.versions);
  };

  // ------------------------------------------------------------------ after a lost run; following
  const recovered = useRef(false);
  useEffect(() => {
    if (recovered.current) return;
    recovered.current = true;
    const run = batch.run;
    if (!run || run.phase === 'done') return;
    // The page was left (or reloaded) during a run: the rows a result already settled keep it (a
    // lost retry); every other document shows its state now (RIB-02).
    const settled = new Map(
      run.results.filter((r) => !rowState(run.task, r).retryable).map((r) => [r.id, r]),
    );
    void reconcile(run.ids.filter((id) => !settled.has(id))).then((fresh) => {
      const byId = new Map(fresh.map((r) => [r.id, r]));
      const results = run.ids.flatMap((id) => {
        const r = settled.get(id) ?? byId.get(id);
        return r ? [r] : [];
      });
      update((s) => ({
        ...refreshItems(s, fresh),
        run: {
          ...run,
          phase: 'done',
          results,
          order: resultOrder(run.task, results),
          lost: true,
        },
      }));
    });
    // Once, on opening the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liveIds =
    view === 'results' && batch.run?.task === 'send' && batch.run.phase === 'done' && !locked
      ? batch.run.results.filter(isLive).map((r) => r.id)
      : [];
  const follow = useQuery({
    queryKey: [...queryKeys.org(orgId), 'sales', 'batch-follow', liveIds.join(',')],
    queryFn: ({ signal }) => salesApi.list(orgId, { ids: liveIds.join(','), limit: 100 }, signal),
    enabled: liveIds.length > 0,
    refetchInterval: 1500,
    staleTime: 0,
  });
  useEffect(() => {
    const docs = follow.data?.items;
    if (!docs?.length) return;
    const byId = new Map(docs.map((d) => [d.id, d]));
    update((s) => {
      if (!s.run) return s;
      let changed = false;
      const results = s.run.results.map((r) => {
        const d = byId.get(r.id);
        if (!d || !r.ok) return r;
        if (d.etaStatus === r.etaStatus && d.status === r.status && d.version === r.version)
          return r;
        changed = true;
        return { ...r, etaStatus: d.etaStatus, status: d.status, version: d.version };
      });
      return changed ? { ...s, run: { ...s.run, results } } : s;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow.data]);

  // The file phase only: leaving would lose the file (`batch.leaveGuard`).
  useEffect(() => {
    if (running !== 'file') return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [running]);

  // ------------------------------------------------------------------ layout
  // RIB-03: step 3 carries no mark until a review of the batch as it is exists; an empty batch
  // needs input; else the review's skips.
  const storedReview = reviewIsCurrent(batch) ? batch.review : null;
  const reviewMark = !items.length
    ? 1
    : storedReview
      ? storedReview.results.filter((r) => !r.ok).length
      : null;
  const position = STEPS.indexOf(step);
  const back = position > 0 ? STEPS[position - 1] : null;
  const forward = position < STEPS.length - 1 ? STEPS[position + 1] : null;
  const environment = org.data?.etaEnvironment;
  // RIB-05: the retry's facts are those of the rows marked «سيُنفَّذ».
  const retryRuns = retry ? [...retry.marks.values()].filter((r) => r.ok) : [];

  const rail = (
    <BatchRail
      task={task}
      items={items}
      locked={locked || undo !== null || hasRun}
      onTakeOut={onTakeOut}
      onClear={() => setDialog('clear')}
    />
  );

  return (
    <Page>
      <header className={salesStyles.hero} data-testid="batch-page">
        <div className={salesStyles.heroRow}>
          <div className={salesStyles.heroText}>
            <span className={salesStyles.heroEyebrow}>
              <Layers size={14} aria-hidden="true" />
              <Link
                to="/orgs/$orgId/sales/invoices"
                params={{ orgId }}
                search={criteria as InvoiceListSearch}
                style={{ color: 'inherit' }}
                data-testid="batch-eyebrow"
              >
                {t.sales.title}
              </Link>
            </span>
            <h1 className={salesStyles.heroTitle} data-testid="batch-title">
              {t.batch.title}
            </h1>
            <p className={salesStyles.heroSub}>
              {t.batch.lead} <MockLabel environment={environment} />
            </p>
          </div>
        </div>
      </header>

      <Stepper<StepId>
        label={t.batch.steps.label}
        steps={STEPS.map((id) => ({
          id,
          title: stepTitles[id],
          issues:
            id === 'task'
              ? task
                ? 0
                : 1
              : id === 'documents'
                ? items.length
                  ? 0
                  : 1
                : reviewMark,
        }))}
        current={step}
        onSelect={(id) => goTo(id)}
        nameOf={(s, n, total) =>
          s.issues === null
            ? t.editor.steps.announce(String(n), String(total), s.title)
            : `${t.editor.steps.announce(String(n), String(total), s.title)} — ${
                s.issues ? t.batch.steps.needsInput : t.editor.steps.clean
              }`
        }
        testId="batch-steps"
        itemTestId={(id) => `batch-step-${id}`}
        className={styles.steps}
        isDisabled={locked}
      />
      <p className="visually-hidden" aria-live="polite" data-testid="batch-announcer">
        {announcement}
      </p>

      <div className={styles.body}>
        {isNarrow ? (
          <BatchRailToggle isExpanded={railOpen} onExpandedChange={setRailOpen}>
            {rail}
          </BatchRailToggle>
        ) : null}
        <section
          className={salesStyles.stepPanel}
          aria-labelledby="batch-step-title"
          data-testid="batch-step-panel"
          data-step={view}
        >
          <h2
            id="batch-step-title"
            ref={headingRef}
            tabIndex={-1}
            className={salesStyles.stepTitle}
          >
            <span className={salesStyles.stepCounter}>
              {t.editor.steps.counter(String(position + 1), String(STEPS.length))}
            </span>
            {view === 'results' ? t.batch.results.title : stepTitles[step]}
          </h2>
          {running ? (
            <div
              className={styles.progress}
              role="status"
              data-testid="batch-progress"
              data-phase={running}
              {...(running === 'file'
                ? { 'data-done': fileProgress.done, 'data-total': fileProgress.total }
                : {})}
            >
              <span>
                {running === 'server'
                  ? t.batch.progress
                  : t.batch.progressFile(String(fileProgress.done), String(fileProgress.total))}
              </span>
              {running === 'server' ? (
                // RIB-09: an indeterminate bar while the server runs (static under reduced motion).
                <span className={styles.busy} aria-hidden="true" data-testid="batch-busy">
                  <span className={styles.busyFill} />
                </span>
              ) : null}
              {running === 'file' ? (
                <span className={styles.progressNote}>{t.batch.leaveGuard}</span>
              ) : null}
            </div>
          ) : null}

          {view === 'task' ? (
            <TaskStep
              tasks={tasks}
              value={task}
              isDisabled={locked}
              onChange={(next) =>
                // A new task keeps the batch and starts a new review (§2); it leaves the results,
                // so unsettled results ask first (RIB-01).
                leaving(() => {
                  setRetry(null);
                  update((s) => setTask(s, next));
                  setNotice(null);
                })
              }
            />
          ) : view === 'documents' ? (
            <DocumentsStep
              orgId={orgId}
              task={task}
              criteria={criteria}
              items={items}
              locked={locked || hasRun}
              lockedByRun={hasRun && !locked}
              onNew={onNew}
              notice={notice}
              onAdd={onAdd}
              onPutAll={onPutAll}
              onBackToTask={() => goTo('task')}
            />
          ) : view === 'review' ? (
            !task ? (
              <div className={styles.block}>
                <p>{t.batch.noTask}</p>
                <div>
                  <Button variant="secondary" onPress={() => goTo('task')}>
                    {t.editor.review.backTo(t.batch.steps.task)}
                  </Button>
                </div>
              </div>
            ) : !items.length ? (
              <div className={styles.block} data-testid="batch-review-empty">
                <p>{t.batch.rail.empty}</p>
                <div>
                  <Button variant="secondary" onPress={() => goTo('documents')}>
                    {t.editor.review.backTo(t.batch.steps.documents)}
                  </Button>
                </div>
              </div>
            ) : dry.isError ? (
              <QueryError error={dry.error} onRetry={() => void dry.refetch()} />
            ) : (
              <BatchReview
                task={task}
                results={review ?? []}
                checking={!review}
                onDropSkipped={onDropSkipped}
              />
            )
          ) : batch.run && (batch.run.phase === 'done' || batch.run.results.length) ? (
            <ResultsPanel
              orgId={orgId}
              task={batch.run.task}
              results={batch.run.results}
              order={batch.run.order}
              lost={Boolean(batch.run.lost)}
              // RIM-07: the marks stay in the table after the dialog closes without running
              // (a phone shows them only then), until the retry runs or the batch starts again.
              retryMarks={retry ? retry.marks : null}
              isBusy={locked || retrying || undo !== null}
              criteria={criteria}
              onRetry={() => void onRetry()}
              onSaveAgain={() => void saveZip(batch.run?.results ?? [])}
              onNew={onNew}
            />
          ) : null}
        </section>
        {isNarrow ? null : rail}
      </div>

      <BatchBar
        backTo={back ? stepTitles[back] : null}
        nextTo={forward ? stepTitles[forward] : null}
        onBack={() => back && goTo(back)}
        onNext={() => forward && goTo(forward)}
        run={view === 'review' && task && review && willRun.length && !undo ? task : null}
        onRun={onRun}
        isBusy={locked}
      >
        {/* RIB-06: the undo bar rides on the step bar, above it at every width (one bottom layer). */}
        {undo ? (
          <div
            className={`${salesStyles.undoBar} ${styles.undoOnBar}`}
            role="status"
            data-testid="undo-bar"
          >
            <span>{t.batch.undo}</span>
            <button type="button" onClick={cancelDiscard} data-testid="undo-delete">
              {t.sales.undo}
            </button>
          </div>
        ) : null}
      </BatchBar>

      <SendConfirm
        isOpen={dialog === 'send'}
        onOpenChange={(open) => !open && setDialog(null)}
        onConfirm={confirmRun}
        facts={facts}
        organization={org.data?.legalName ?? org.data?.name ?? null}
        environment={environment}
      />
      <DiscardConfirm
        isOpen={dialog === 'discard'}
        onOpenChange={(open) => !open && setDialog(null)}
        onConfirm={confirmRun}
        facts={facts}
      />
      <RetryConfirm
        isOpen={dialog === 'retry'}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        onConfirm={confirmRetry}
        task={batch.run?.task ?? 'send'}
        count={retryRuns.length}
        total={sumDecimals(retryRuns.map((r) => r.totalAmount ?? '0'))}
        organization={org.data?.legalName ?? org.data?.name ?? null}
        environment={environment}
        isPhone={isPhone}
      />
      <ClearConfirm
        isOpen={dialog === 'clear'}
        onOpenChange={(open) => !open && setDialog(null)}
        onConfirm={onClear}
      />
      <LeaveResultsConfirm
        isOpen={dialog === 'leave'}
        onOpenChange={(open) => {
          if (open) return;
          leaveThen.current = null;
          setDialog(null);
        }}
        onConfirm={confirmLeave}
      />
    </Page>
  );
}

/** A batch item as the fields of a result row. */
function snapshotOf(item: BatchItem | undefined): Partial<BulkResult> {
  if (!item) return {};
  return {
    number: item.number,
    status: item.status,
    etaStatus: item.etaStatus,
    readiness: item.readiness,
    version: item.version,
    totalAmount: item.totalAmount,
    receiverName: item.receiverName,
    cash: item.cash,
  };
}

/** `billing-documents-YYYYMMDD-HHmm.zip` (local time). */
function zipName(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `billing-documents-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}.zip`;
}

/** The document's PDF as it is now (`GET …/:id/pdf`, the single action's own route). */
async function fetchPdf(orgId: string, id: string): Promise<Uint8Array> {
  let response: Response;
  try {
    response = await fetch(
      `/api/orgs/${encodeURIComponent(orgId)}/sales/documents/${encodeURIComponent(id)}/pdf`,
      { credentials: 'same-origin' },
    );
  } catch {
    throw Object.assign(new Error('network'), { code: 'network_error' });
  }
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { code?: string } }).error.code ?? 'internal_error')
        : 'internal_error';
    throw Object.assign(new Error(code), { code });
  }
  return new Uint8Array(await response.arrayBuffer());
}
